import { filter, includes, map, reject } from 'lodash';
import { Controller } from '@hotwired/stimulus'

import { closeFlashAlert, closeFlashInfo, flashAlert, flashInfo } from '../../shared/flash'

export default class extends Controller {

  static targets = [
    'container',
    'loadingContainer',
    'checkoutContainer',
    'completeContainer',
    'cartContainer',
  ]

  initialize() {
    this.stripe = window.Stripe
    this.isFormComplete = false;
    this.isCustomerFormComplete = false;
    this.isTipComplete = false;
    this.isPaymentFormComplete = false;
    this.isCardFormComplete = false;
    this.notes = null;
  }

  connect() {
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }
    this.order = { id: this.containerTarget.dataset.orderId };

    this.containerTarget.addEventListener('customerUpdate', this.onCustomerUpdate);
    this.containerTarget.addEventListener('tipUpdate', this.onTipUpdate);
    this.containerTarget.addEventListener('paymentUpdate', this.onPaymentUpdate);
    this.containerTarget.addEventListener('intentUpdateStart', this.onUpdateIntentStart);
    this.containerTarget.addEventListener('noteUpdate', this.onNoteUpdate);
    this.containerTarget.addEventListener('orderLoaded', this.onOrderLoad);
    this.containerTarget.addEventListener('submitOrder', this.submitOrder);
  }

  onUpdateIntentStart = _event => {
    this.isPaymentFormComplete = false
  }

  onNoteUpdate = ({ detail }) => {
    this.notes = detail.notes
  }

  makePayments = () => {
    const cardRequests = filter(this.checks, ['paymentMethod', 'web_intent'])
    if (cardRequests.length === 0) {
      this.completeOrder()
      return
    }

    flashInfo('Confirming credit card payment(s), please wait...')
    const stripeConfirmationParams = cardRequests.map(check => ({
      secret: check.clientSecret,
      card: check.card,
      billing_details: {
        name: check.cardholderName || `${this.first_name} ${this.last_name}`,
        email: `${this.email}`,
        phone: `${this.phone}`
      },
      metadata: {
        order_id: this.order.id
      }
    }))
    // NOTE: Stripe will not allow simultaneous card confirmations via Promise.all - returns an error for at least one.
    // This uses reduce to run each confirmation in order, waiting for previous to resolve.
    stripeConfirmationParams.reduce((promiseChain, currentParam) => {
      return promiseChain.then(chainResults => {
        const secret = currentParam.secret
        delete currentParam.secret

        return this.stripe.confirmCardPayment(secret, { payment_method: currentParam }).then(currentResult => {
          this.checks.forEach(check => {
            if (check.clientSecret && check.clientSecret === currentResult.paymentIntent?.client_secret) {
              check.webIntentId = currentResult.paymentIntent?.id
              check.amountCents = currentResult.paymentIntent?.amount
            }
          })
          return [...chainResults, currentResult]
        })
      })
    }, Promise.resolve([]))
      .then(results => {
        closeFlashInfo()
        const intentResults = filter(results, 'paymentIntent').map(result => result.paymentIntent)
        const successfulIntents = filter(intentResults, ['status', 'succeeded'])
        const badRequestIntents = reject(intentResults, ['status', 'succeeded'])
        const cardErrors = filter(results, 'error')

        if (successfulIntents.length === results.length) {
          this.completeOrder()
        } else {
          if (badRequestIntents.length + cardErrors.length === results.length) {
            // all cards have failed, do not complete order!
            flashAlert('Card payment(s) failed, please try again.')
            this.dispatchCardErrors(cardErrors)
            this.loadingContainerTarget.classList.add('hidden');
            this.checkoutContainerTarget.classList.remove('hidden');
          } else {
            // we cannot abort the card success(es), filter failed intents and complete order with error message.
            const badIds = map(badRequestIntents, 'id').concat(map(cardErrors, ['paymentIntent', 'id']))
            this.checks = reject(this.checks, check => check.webIntentId && includes(badIds, check.webIntentId))
            this.completePageErrorMessage =
              'There was a problem with a card payment. The other transactions have been completed. View order for outstanding amount.'
            this.completeOrder()
          }
        }
      })
      .catch(err => this.onOrderError(err))
  }

  dispatchCardErrors(cardErrors) {
    const detail = cardErrors.map(result => {
      const { code, decline_code, message, payment_intent, type } = result.error
      return { code, declineCode: decline_code, message, type, webIntentId: payment_intent?.id }
    })
    const cardErrorEvent = new CustomEvent('cardErrors', { detail })
    this.containerTarget.dispatchEvent(cardErrorEvent)
  }

  onOrderLoad = ({ detail }) => {
    this.order = detail.order;
    this.order.id = detail?.orderId;
    this.destinationId = this.order?.destination_id
    this.amount = this.order?.total
  }

  onCustomerUpdate = ({ detail }) => {
    this.firstName = detail?.first_name;
    this.lastName = detail?.last_name;
    this.email = detail?.email;
    this.phone = detail?.phone;

    if (this.firstName && this.lastName && this.email && this.phone) {
      this.isCustomerFormComplete = true;
    } else {
      this.isCustomerFormComplete = false;
    }
    this.validateCheckoutForm();
  }

  onTipUpdate = () => {
    this.isTipComplete = true;
    this.dispatchAmountChange();
    this.validateCheckoutForm();
  }

  dispatchAmountChange = () => {
    const amountChangeEvent = new CustomEvent('amountChange', {})
    this.containerTarget.dispatchEvent(amountChangeEvent)
  }

  validateCheckoutForm = () => {
    this.isFormComplete = this.isPaymentFormComplete && this.isCustomerFormComplete && this.isTipComplete;
    const validateCheckoutFormEvent = new CustomEvent('validateCheckoutForm', { detail: { valid: this.isFormComplete } })
    this.containerTarget.dispatchEvent(validateCheckoutFormEvent)
  }

  onPaymentUpdate = ({ detail }) => {
    this.checks = detail?.checks
    this.isPaymentFormComplete = detail?.isPaymentFormComplete
    this.validateCheckoutForm();
  }

  onOrderError(error) {
    closeFlashInfo()
    console.error(error);
    let message = error.message || error.statusText || error.error || 'Something went wrong, please try again.'
    flashAlert(message);

    this.checkoutContainerTarget.classList.remove('hidden');
    this.loadingContainerTarget.classList.add('hidden');
  }

  submitOrder = event => {
    event.preventDefault();
    closeFlashAlert()
    this.checkoutContainerTarget.classList.add('hidden');
    this.loadingContainerTarget.classList.remove('hidden');
    this.makePayments()
  }

  completeOrder = () => {
    closeFlashAlert();
    const customer = {
      first_name: this.firstName,
      last_name: this.lastName,
      email: this.email,
      phone: this.phone,
    }
    const checks = this.checks.map(check => {
      return {
        payment_method: check.paymentMethod,
        web_intent_id: check.webIntentId,
        amount: check.amountCents,
      }
    })
    const requestParams = { customer, checks, notes: this.notes }

    fetch(
      `/sandbar/admin/orders/${this.order.id}/complete`,
      {
        method: 'PUT',
        headers: this.fetchHeaders,
        body: JSON.stringify(requestParams),
      }
    )
      .then(resp => {
        if (resp.ok) {
          this.showOrderSuccess()
        } else {
          resp.json().then(err => this.onOrderError(err))
          this.completeContainerTarget.classList.add('hidden');
          this.loadingContainerTarget.classList.add('hidden');
          this.checkoutContainerTarget.classList.remove('hidden');
        }
      })
      .catch(err => this.onOrderError(err));
  }

  showOrderSuccess() {
    closeFlashInfo()
    if (this.completePageErrorMessage) {
      flashAlert(this.completePageErrorMessage)
    }
    this.loadingContainerTarget.classList.add('hidden');
    this.checkoutContainerTarget.classList.add('hidden');
    this.completeContainerTarget.classList.remove('hidden');
    this.cartContainerTarget.classList.add('hidden');
  }
}
